/**
 * Modal dialog to enter an email to invite as member.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2026
 */
import { fetchUserComplete, inviteSubUser } from '@/api'
import ModalDialog from '@/components/ModalDialog'
import { RhfFormInput } from '@/components/RhfFormInput'
import { logger } from '@/config/logger'
import { Util } from '@amplify3/duplo-back-end'
import { zodResolver } from '@hookform/resolvers/zod'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { BiLoaderAlt } from 'react-icons/bi'
import { z as zod } from 'zod'
import './InviteMemberDialog.css'

/**
 * The Zod schema defining the fields and their data type.
 */
const InviteSchema = zod.object({
  firstName: zod.string().trim().min(1, 'Required'),
  lastName: zod.string().trim().min(1, 'Required'),
  email: zod.email().trim().min(1, 'Required'),
})
type InviteType = zod.infer<typeof InviteSchema>

interface InviteMemberDialogProps {
  company_id: string
  isShowing: boolean
  show: (isShowing: boolean) => void
}

/**
 * A modal dialog to enter an email to invite as member.
 */
const InviteMemberDialog = (props: InviteMemberDialogProps) => {
  const [isProcessing, setProcessing] = useState(false)
  const [requestError, setRequestError] = useState<string | null>(null)

  // Get the needed functions defined by react-use-form
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<InviteType>({
    mode: 'all',
    resolver: zodResolver(InviteSchema),
    defaultValues: { firstName: '', lastName: '', email: '' },
  })

  /**
   * Clear error message at the opening of the dialog.
   */
  useEffect(() => {
    if (props.isShowing) {
      reset()
      setRequestError(null)
    }
  }, [props.isShowing])

  /**
   * Handle the sending of the invitation.
   */
  const submitInvitation = async (values: InviteType) => {
    if (!isProcessing) {
      setProcessing(true)
      try {
        logger.info(`Send invitation to ${values.email}`)
        await inviteSubUser({
          company_id: props.company_id,
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
        })
        fetchUserComplete({ forceRefresh: true })
        props.show(false)
        setRequestError(null)
      } catch (err) {
        const message = axios.isAxiosError(err)
          ? (err.response?.data?.message ?? 'Failed to send invitation.')
          : 'Failed to send invitation.'
        setRequestError(message)
      } finally {
        await Util.sleep(500)
        setProcessing(false)
      }
    }
  }
  const onSubmit = handleSubmit(submitInvitation)

  return (
    <ModalDialog
      className="invite-dialog"
      title="Invite member"
      isVisible={props.isShowing}
      changeVisible={props.show}
      maxWidth={'sm'}
      contents={
        <div className="contents">
          <form onSubmit={onSubmit}>
            <RhfFormInput
              title="First Name"
              id="firstName"
              type="string"
              register={register}
              error={errors.firstName}
            />
            <RhfFormInput
              title="Last Name"
              id="lastName"
              type="string"
              register={register}
              error={errors.lastName}
            />
            <RhfFormInput
              title="Email"
              id="email"
              type="string"
              register={register}
              error={errors.email}
            />

            <div className="error-display">{requestError}</div>

            <div className="invite-actions">
              <button
                type="submit"
                className="send"
                disabled={!isValid || isProcessing}
              >
                {isProcessing ? (
                  <BiLoaderAlt className="loadingIcon" />
                ) : (
                  'Send'
                )}
              </button>
              <button
                type="button"
                className="cancel"
                onClick={() => props.show(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      }
    ></ModalDialog>
  )
}

export default InviteMemberDialog
